/**
 * <CorrectionChallenge> — le « Défi éclair » : de quoi jouer pendant que Sam corrige la copie
 * (organism).
 *
 * Le défi de l'ancien code (`PendingAction.js`), sans ses points : une série de questions sur ce
 * que l'élève travaille (envoyées par le serveur avec la copie), la série des bonnes réponses
 * d'affilée, et un score à la fin. Chaque réponse part au serveur, qui ne repose plus ce que
 * l'élève sait. Le défi dure ce que dure l'attente : il disparaît avec elle.
 *
 * Contrôlé : où en est le défi arrive en props (la question, la réponse choisie, la série), les
 * gestes remontent. Deux phases :
 *  - `question` : l'intitulé, « 2 / 3 », la série quand elle atteint deux, la question, ses
 *    réponses ; une fois répondue, le mot qui dit si c'était juste, et la suite ;
 *  - `done` : le score, la meilleure série. Pas de « Rejouer » : la série remontrerait les mêmes
 *    questions le même jour, ce que la banque ne fait plus.
 * D'une question à la suivante, et vers le score, l'une sort et l'autre entre (`<StepStage>`) ; la
 * réponse donnée, elle, se pose sur la même question sans rien rejouer.
 *
 * Ce qui change : la flamme de la série est le glyphe `fire` de l'interface dans une chip, à la
 * place de l'emoji ; la meilleure série s'écrit sans emoji.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { StepStage } from "../../atoms/StepStage"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { QuizChoiceRow } from "../../molecules/QuizChoiceRow"
import { quizChoiceLetter } from "../../molecules/QuizChoiceRow/QuizChoiceRow"
import { RichText } from "../../molecules/RichText"
import { type UIChallengeQuestion } from "../../../ports/primary/ui/types/studentActivity"
import { CHALLENGE_GAME_TEXTS, CHALLENGE_STREAK_FROM, challengeChoiceState } from "./challengeGame"

// Une question du défi : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UIChallengeQuestion }

/** Où en est le défi. */
export type CorrectionChallengeState =
    | {
          phase: "question"
          /** La question posée, et sa place dans la série (0 pour la première). */
          question: UIChallengeQuestion
          index: number
          total: number
          /** La réponse choisie ; `null` tant que l'élève n'a pas répondu. */
          selected: number | null
          /** Les bonnes réponses d'affilée, réponse en cours comprise. */
          streak: number
      }
    | { phase: "done"; right: number; total: number; bestStreak: number }

export type CorrectionChallengeProps = {
    state: CorrectionChallengeState
    /** Une réponse touchée. */
    onAnswer: (index: number) => void
    /** « Question suivante » ou « Voir mon score ». */
    onNext: () => void
    testID?: string
}

const TEXTS = {
    ...CHALLENGE_GAME_TEXTS,
    title: "Défi éclair",
    done: "Défi terminé",
    bestStreak: (n: number) => `Meilleure série x${n}`,
}

export function CorrectionChallenge(props: CorrectionChallengeProps) {
    const { state, testID } = props
    // Les questions s'enchaînent comme les étapes : la précédente sort, la suivante entre.
    return (
        <StepStage stepKey={state.phase === "done" ? "done" : `question-${state.index}`} testID={testID}>
            <ChallengeStep {...props} />
        </StepStage>
    )
}

function ChallengeStep(props: CorrectionChallengeProps) {
    const { state, onAnswer, onNext } = props

    if (state.phase === "done") {
        return (
            <Stack gap="3" align="center">
                <SectionLabel>{TEXTS.done}</SectionLabel>
                <Text size="text-3xl" weight="bold" color="text-strong">
                    {TEXTS.score(state.right, state.total)}
                </Text>
                {state.bestStreak >= CHALLENGE_STREAK_FROM && (
                    <Text size="text-md" color="text-muted">
                        {TEXTS.bestStreak(state.bestStreak)}
                    </Text>
                )}
            </Stack>
        )
    }

    const { question, index, total, selected, streak } = state
    const answered = selected != null
    const isRight = selected === question.correctIndex
    const last = index + 1 >= total

    return (
        <Stack gap="3">
            <Row justify="between" align="center" style={styles.head}>
                <Row gap="2" align="center">
                    <SectionLabel>{TEXTS.title}</SectionLabel>
                    <Text size="text-sm" weight="semibold" color="text-muted">
                        {TEXTS.count(index, total)}
                    </Text>
                </Row>
                {streak >= CHALLENGE_STREAK_FROM && (
                    <Badge variant="warning" iconName="fire" text={TEXTS.streak(streak)} />
                )}
            </Row>

            {question.textFormat === "rich" ? (
                <RichText value={question.question} />
            ) : (
                <Text size="text-xl" weight="bold" color="text-strong" center>
                    {question.question}
                </Text>
            )}

            <Stack gap="2">
                {question.choices.map((choice, i) => (
                    <QuizChoiceRow
                        key={`${i}-${choice}`}
                        letter={quizChoiceLetter(i)}
                        label={choice}
                        labelFormat={question.choiceFormats?.[i]}
                        state={challengeChoiceState(i, question.correctIndex, selected)}
                        onPress={() => onAnswer(i)}
                    />
                ))}
            </Stack>

            {answered && (
                <Stack gap="3">
                    <Text size="text-md" weight="bold" color={isRight ? "success-strong" : "text-muted"} center>
                        {isRight ? TEXTS.right : TEXTS.wrong}
                    </Text>
                    <Button label={last ? TEXTS.seeScore : TEXTS.next} onPress={onNext} fullWidth />
                </Stack>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    /** La chip de la série apparaît sans pousser la question : la ligne garde sa hauteur. */
    head: { minHeight: 26 },
}))
